import _ from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import { Box } from 'reflexbox';
import {
  Heading,
  Message,
  Text,
} from 'rebass';
import { Link } from 'react-router';

import config from 'frontend/config';

import If from 'frontend/components/If';

import SimcInput from './SimcInput';
import SimcLog from './SimcLog';

const errorMessages = {
  'time-limit': 'Your simulation exceeded the time limit',
  'advanced-error': 'Advanced sim failure - check SimC output to determine what went wrong',
  'server-error': 'A server error occurred',
  'unknown-error': 'A simulation error occurred',
};

const ReportError = ({ report, output }) => {
  const reportErrorType = _.get(report, 'error.type', 'unknown-error');
  return (
    <Box>
      <Heading color={config.colors.red}>Simulation Failed</Heading>
      <Text color={config.colors.red} bold>
        {errorMessages[reportErrorType] || 'Unknown reason! Seri will look into it'}
      </Text>
      <Text small mt={1}>
        Simc Version: {report.simbot.simcVersion}
        <If
          condition={report.simbot.simcGitHash}
          then={() => (
            <>
              {' '}
              (
              <a
                href={`https://github.com/simulationcraft/simc/commit/${report.simbot.simcGitHash}`}
                target="_blank"
                rel="noreferrer noopener"
              >
                {report.simbot.simcGitHash.substr(0, 7)}
              </a>
              )
            </>
          )}
        />
      </Text>

      <If condition={reportErrorType === 'time-limit'}>
        <Box>
          <Heading level={3} mt={3}>Troubleshooting</Heading>

          <ul>
            <li>Try lowering the iterations or fight length</li>
            <li>Use a simpler fight style</li>
            <li>
              For advanced simulations, try fewer actors/copy commands or split
              them over multiple simulations
            </li>
            <li>
              <Link
                to="/account"
                href="/account"
              >
                Get Raidbots Premium
              </Link>
              {' '}
              to get higher time limits
            </li>
            <li>
              If you think you&apos;ve run into a bug, contact Seri using the
              links in the footer for further debugging
            </li>
          </ul>
        </Box>
      </If>

      <If condition={_.get(report, 'simbot.meta.nonMaxLevel', false)}>
        <Message theme="warning" p={1} mt={2}>
          Non-max level characters are not fully supported
        </Message>
      </If>

      <If condition={reportErrorType === 'advanced-error'}>
        <Box>
          <Heading level={3} mt={3}>Troubleshooting</Heading>

          <ul>
            <li>
              Check the advanced simulation output for a SimC error. Most advanced
              sim errors result from invalid SimC input (bad commands, syntax errors, etc)
            </li>
            <li>
              Tip: You can verify that you have valid input by using SimC locally to
              make sure it runs. Once you&apos;ve worked out any bugs, you can resubmit.
            </li>
            <li>
              If you think you&apos;ve run into a bug, ask around in the Raidbots or SimcMinMax Discords.
            </li>
          </ul>
        </Box>
      </If>

      <If condition={reportErrorType === 'server-error'}>
        <Box>
          <Text my={2}>
            Something bad happened with the servers. Try your sim again in a few minutes.
          </Text>
          <Text my={2}>
            If this problem persists, you can try contacting Seri using the links at the bottom of this page.
          </Text>
        </Box>
      </If>

      <If condition={reportErrorType === 'unknown-error'}>
        <Box>
          <Heading level={3} mt={3}>Troubleshooting</Heading>

          <Text>
            SimulationCraft encountered an error.
          </Text>

          <ul>
            <li>
              <Text>
                Check the SimulationCraft Log below to see if there is an error you can diagnose.
              </Text>
            </li>
            <li>
              <Text>
                Try using a different SimC Version (under Simulation Options).
              </Text>
            </li>
            <li>
              <Text>
                If this problem persists, check the Raidbots Discord for more information.
              </Text>
            </li>
          </ul>
        </Box>
      </If>

      <Box mt={4}>
        <SimcLog log={output} open />
        <SimcInput report={report} simId={report?.simbot?.simId} />
      </Box>
    </Box>
  );
};

ReportError.propTypes = {
  report: PropTypes.shape({
    simbot: PropTypes.object,
    error: PropTypes.shape({
      type: PropTypes.string,
    }),
  }).isRequired,
  output: PropTypes.string.isRequired,
};

export default ReportError;
